import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import {
  MessageSquare, Heart, Reply, Trash2, EyeOff, Eye,
  RefreshCw, Filter, CheckCircle2, AtSign,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter, Youtube } from "lucide-react";

const platformIcons: Record<string, React.ElementType> = {
  instagram: Instagram,
  twitter: Twitter,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
  threads: AtSign,
};

const platformColors: Record<string, string> = {
  instagram: "text-pink-500",
  twitter: "text-slate-800",
  facebook: "text-blue-600",
  linkedin: "text-blue-700",
  youtube: "text-red-600",
  threads: "text-slate-700",
  tiktok: "text-black",
};

type Comment = {
  id: string;
  platform: string;
  platform_comment_id: string;
  author_name?: string;
  author_handle?: string;
  author_avatar?: string;
  content: string;
  like_count: number;
  reply_count: number;
  is_reply: boolean;
  is_hidden: boolean;
  is_deleted: boolean;
  replied_at?: string;
  our_reply?: string;
  created_at_platform?: string;
  fetched_at: string;
  social_account_id: string;
  post_id: string;
};

type FilterMode = "all" | "unreplied" | "hidden";

export const CommentsInboxView = () => {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [platformFilter, setPlatformFilter] = useState<string>("all");

  const { data: comments = [], isLoading, refetch } = useQuery({
    queryKey: ["post-comments", orgId, filter, platformFilter],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = (supabase as any)
        .from("post_comments")
        .select("*")
        .eq("is_deleted", false)
        .order("fetched_at", { ascending: false })
        .limit(100);
      if (orgId) q = q.eq("org_id", orgId);
      if (filter === "unreplied") q = q.is("replied_at", null);
      if (filter === "hidden") q = q.eq("is_hidden", true);
      if (platformFilter !== "all") q = q.eq("platform", platformFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Comment[];
    },
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ["social-accounts-for-comments"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data } = await supabase
        .from("social_accounts")
        .select("id, platform")
        .eq("user_id", user.id)
        .eq("is_connected", true);
      return data ?? [];
    },
  });

  const replyMutation = useMutation({
    mutationFn: async ({ commentId, socialAccountId, text }: { commentId: string; socialAccountId: string; text: string }) => {
      const { error } = await supabase.functions.invoke("reply-to-comment", {
        body: { commentId, socialAccountId, replyText: text },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Reply sent", description: "Your reply was posted successfully." });
      setReplyingTo(null);
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["post-comments"] });
    },
    onError: (err: any) => {
      toast({ title: "Reply failed", description: err.message, variant: "destructive" });
    },
  });

  const moderateMutation = useMutation({
    mutationFn: async ({ commentId, action, socialAccountId }: { commentId: string; action: string; socialAccountId: string }) => {
      const { error } = await supabase.functions.invoke("moderate-comment", {
        body: { commentId, action, socialAccountId },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Done", description: "Comment moderated." });
      qc.invalidateQueries({ queryKey: ["post-comments"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    },
  });

  const fetchCommentsMutation = useMutation({
    mutationFn: async () => {
      // Fetch comments for all posts with a platform_post_id
      const { data: posts } = await (supabase as any)
        .from("posts")
        .select("id, social_account_ids")
        .eq("status", "published")
        .not("platform_post_id", "is", null)
        .limit(20);

      if (!posts) return;
      await Promise.allSettled(
        posts.map((p: any) =>
          supabase.functions.invoke("fetch-post-comments", {
            body: { postId: p.id, socialAccountId: p.social_account_ids?.[0] },
          })
        )
      );
    },
    onSuccess: () => {
      toast({ title: "Comments synced", description: "Latest comments fetched from all platforms." });
      refetch();
    },
  });

  const uniquePlatforms = [...new Set(accounts.map((a: any) => a.platform))];
  const unrepliedCount = comments.filter(c => !c.replied_at && !c.is_hidden).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Comments Inbox</h2>
          <p className="text-muted-foreground">Manage and reply to comments across all platforms</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => fetchCommentsMutation.mutate()}
            disabled={fetchCommentsMutation.isPending}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${fetchCommentsMutation.isPending ? "animate-spin" : ""}`} />
            Sync Comments
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 border rounded-lg p-1">
          {(["all", "unreplied", "hidden"] as FilterMode[]).map(f => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "ghost"}
              onClick={() => setFilter(f)}
              className="h-7 text-xs capitalize"
            >
              {f === "unreplied" ? `Unreplied${unrepliedCount > 0 ? ` (${unrepliedCount})` : ""}` : f}
            </Button>
          ))}
        </div>

        <div className="flex gap-1 border rounded-lg p-1">
          <Button
            size="sm"
            variant={platformFilter === "all" ? "default" : "ghost"}
            onClick={() => setPlatformFilter("all")}
            className="h-7 text-xs"
          >
            All
          </Button>
          {uniquePlatforms.map(p => {
            const Icon = platformIcons[p] ?? MessageSquare;
            return (
              <Button
                key={p}
                size="sm"
                variant={platformFilter === p ? "default" : "ghost"}
                onClick={() => setPlatformFilter(p)}
                className="h-7 text-xs capitalize"
              >
                <Icon className={`w-3 h-3 mr-1 ${platformColors[p] ?? ""}`} />
                {p}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Comments List */}
      {isLoading ? (
        <Card className="p-8 text-center text-muted-foreground">Loading comments…</Card>
      ) : comments.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground">No comments yet. Sync to fetch the latest from all platforms.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {comments.map(comment => {
            const Icon = platformIcons[comment.platform] ?? MessageSquare;
            const displayName = comment.author_name || (comment.author_handle ? `@${comment.author_handle}` : "Unknown");
            const initials = displayName.slice(0, 2).toUpperCase();
            const isReplying = replyingTo === comment.id;

            return (
              <Card
                key={comment.id}
                className={`p-4 transition-all ${comment.is_hidden ? "opacity-50" : ""} ${comment.replied_at ? "border-green-200 dark:border-green-900" : ""}`}
              >
                <div className="flex gap-3">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarImage src={comment.author_avatar} />
                    <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-medium text-sm">{displayName}</span>
                      <Badge variant="outline" className="h-5 text-[11px] gap-1 px-1.5">
                        <Icon className={`w-3 h-3 ${platformColors[comment.platform] ?? ""}`} />
                        {comment.platform}
                      </Badge>
                      {comment.is_reply && (
                        <Badge variant="secondary" className="h-5 text-[11px]">reply</Badge>
                      )}
                      {comment.replied_at && (
                        <Badge className="h-5 text-[11px] bg-green-100 text-green-700 border-green-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Replied
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {comment.created_at_platform
                          ? formatDistanceToNow(new Date(comment.created_at_platform), { addSuffix: true })
                          : formatDistanceToNow(new Date(comment.fetched_at), { addSuffix: true })}
                      </span>
                    </div>

                    <p className="text-sm text-foreground/90 mb-2">{comment.content}</p>

                    {comment.like_count > 0 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                        <Heart className="w-3 h-3" />
                        {comment.like_count.toLocaleString()} likes
                      </div>
                    )}

                    {comment.our_reply && (
                      <div className="mt-2 pl-3 border-l-2 border-green-400 text-sm text-muted-foreground">
                        <span className="font-medium text-green-600">You replied: </span>
                        {comment.our_reply}
                      </div>
                    )}

                    {/* Reply Box */}
                    {isReplying && (
                      <div className="mt-3 space-y-2">
                        <Textarea
                          placeholder="Write your reply…"
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          rows={2}
                          className="text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() =>
                              replyMutation.mutate({
                                commentId: comment.platform_comment_id,
                                socialAccountId: comment.social_account_id,
                                text: replyText,
                              })
                            }
                            disabled={!replyText.trim() || replyMutation.isPending}
                          >
                            {replyMutation.isPending ? "Sending…" : "Send Reply"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(""); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-1 mt-2">
                      {!isReplying && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => { setReplyingTo(comment.id); setReplyText(""); }}
                        >
                          <Reply className="w-3 h-3 mr-1" />
                          Reply
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() =>
                          moderateMutation.mutate({
                            commentId: comment.platform_comment_id,
                            action: comment.is_hidden ? "unhide" : "hide",
                            socialAccountId: comment.social_account_id,
                          })
                        }
                        disabled={moderateMutation.isPending}
                      >
                        {comment.is_hidden ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                        {comment.is_hidden ? "Unhide" : "Hide"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-destructive hover:text-destructive"
                        onClick={() =>
                          moderateMutation.mutate({
                            commentId: comment.platform_comment_id,
                            action: "delete",
                            socialAccountId: comment.social_account_id,
                          })
                        }
                        disabled={moderateMutation.isPending}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
